import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { GlassCard, PageLoader } from "@/components/shared";
import { HardDrive, MapPin, Shield, CalendarClock, CreditCard, ArrowRight, ArrowLeft, Check, Loader2, Lock, Zap } from "lucide-react";

const CONNECTIONS = [
  { key: "wireguard", icon: Shield, title: "WireGuard Private Tunnel", desc: "Recommended. Encrypted private tunnel between Proxmox and your backup server.", badge: "Recommended" },
  { key: "tls", icon: Lock, title: "TLS Direct", desc: "Direct TLS-encrypted connection over the public internet.", badge: null },
];

const RETENTIONS = [
  { key: "standard", title: "Standard", desc: "30 daily · 12 monthly" },
  { key: "business", title: "Business", desc: "14 daily · 8 weekly · 12 monthly" },
  { key: "longterm", title: "Long Term", desc: "30 daily · 12 weekly · 24 monthly" },
];

const STEP_META = [
  ["Storage", HardDrive], ["Region", MapPin], ["Connection", Shield], ["Retention", CalendarClock], ["Checkout", CreditCard],
];

export default function Deploy() {
  const [step, setStep] = useState(0);
  const [plans, setPlans] = useState(null);
  const [regions, setRegions] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [cfg, setCfg] = useState({ storage_tb: 5, region: "us-west", connection: "wireguard", retention: "standard" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data));
    api.get("/regions").then((r) => setRegions(r.data));
  }, []);
  if (!plans || !regions.length) return <PageLoader />;

  const planForTb = (tb) => plans.find((p) => p.storage_tb >= tb) || plans[plans.length - 1];
  const plan = planForTb(cfg.storage_tb);
  const price = cycle === "annual" ? plan.price_annual : plan.price_monthly;

  const checkout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_key: plan.key, cycle, origin_url: window.location.origin,
        deployment: { region: cfg.region, connection: cfg.connection, retention: cfg.retention },
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-display text-2xl font-bold text-slate-50">Deploy a backup server</h1>
      <p className="text-sm text-slate-400">A fully managed Proxmox Backup Server, live in minutes.</p>

      {/* stepper */}
      <div className="my-6 flex items-center gap-2">
        {STEP_META.map(([label, Icon], i) => (
          <div key={label} className="flex flex-1 items-center gap-2">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border text-xs ${i < step ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : i === step ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-600"}`}>
              {i < step ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
            </div>
            {i < STEP_META.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-emerald-500/40" : "bg-slate-800"}`} />}
          </div>
        ))}
      </div>

      <GlassCard className="p-6">
        {step === 0 && (
          <div data-testid="wizard-step-storage">
            <h2 className="font-display text-lg font-semibold text-slate-100">How much backup storage do you need?</h2>
            <div className="mt-6 flex items-end justify-between">
              <span className="font-mono text-4xl font-bold text-cyan-300">{cfg.storage_tb} TB</span>
              <span className="text-sm text-slate-500">Recommends <b className="text-slate-300">{plan.name}</b></span>
            </div>
            <Slider min={1} max={20} step={1} value={[cfg.storage_tb]} onValueChange={(v) => setCfg({ ...cfg, storage_tb: v[0] })} className="my-5" data-testid="wizard-storage-slider" />
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 5, 10].map((tb) => (
                <button key={tb} onClick={() => setCfg({ ...cfg, storage_tb: tb })} className={`rounded-lg border py-2 text-sm ${cfg.storage_tb === tb ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}>{tb} TB</button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="wizard-step-region">
            <h2 className="font-display text-lg font-semibold text-slate-100">Where should your backups live?</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {regions.map((r) => (
                <button key={r.key} onClick={() => setCfg({ ...cfg, region: r.key })} data-testid={`wizard-region-${r.key}`}
                  className={`rounded-lg border p-4 text-left ${cfg.region === r.key ? "border-cyan-500/50 bg-cyan-500/10" : "border-slate-800 hover:border-slate-600"}`}>
                  <div className="flex items-center justify-between"><span className="font-semibold text-slate-100">{r.label}</span><span className="font-mono text-xs text-emerald-400">{r.latency}</span></div>
                  <div className="mt-1 text-xs text-slate-500">{r.location}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-connection">
            <h2 className="font-display text-lg font-semibold text-slate-100">Secure connection</h2>
            <div className="mt-5 space-y-3">
              {CONNECTIONS.map((c) => (
                <button key={c.key} onClick={() => setCfg({ ...cfg, connection: c.key })} data-testid={`wizard-conn-${c.key}`}
                  className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left ${cfg.connection === c.key ? "border-cyan-500/50 bg-cyan-500/10" : "border-slate-800 hover:border-slate-600"}`}>
                  <c.icon className="mt-0.5 h-5 w-5 text-cyan-400" />
                  <div>
                    <div className="flex items-center gap-2"><span className="font-semibold text-slate-100">{c.title}</span>{c.badge && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300">{c.badge}</span>}</div>
                    <p className="mt-1 text-sm text-slate-400">{c.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-retention">
            <h2 className="font-display text-lg font-semibold text-slate-100">Retention policy</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {RETENTIONS.map((r) => (
                <button key={r.key} onClick={() => setCfg({ ...cfg, retention: r.key })} data-testid={`wizard-retention-${r.key}`}
                  className={`rounded-lg border p-4 text-left ${cfg.retention === r.key ? "border-cyan-500/50 bg-cyan-500/10" : "border-slate-800 hover:border-slate-600"}`}>
                  <div className="font-semibold text-slate-100">{r.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{r.desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="wizard-step-checkout">
            <h2 className="font-display text-lg font-semibold text-slate-100">Review & checkout</h2>
            <div className="mt-5 flex gap-2 rounded-lg border border-slate-800 p-1 w-fit">
              {["monthly", "annual"].map((c) => (
                <button key={c} onClick={() => setCycle(c)} className={`rounded-md px-4 py-1.5 text-sm capitalize ${cycle === c ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400"}`}>{c}{c === "annual" && " (2 mo free)"}</button>
              ))}
            </div>
            <div className="mt-5 space-y-2 rounded-lg border border-slate-800 bg-[#06080D] p-4 text-sm">
              {[["Plan", plan.name], ["Storage", `${plan.storage_tb} TB`], ["Region", regions.find((r) => r.key === cfg.region)?.label], ["Connection", cfg.connection === "wireguard" ? "WireGuard" : "TLS Direct"], ["Retention", cfg.retention], ["Billing", cycle]].map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-slate-500">{k}</span><span className="text-slate-200 capitalize">{v}</span></div>
              ))}
              <div className="mt-2 flex justify-between border-t border-slate-800 pt-2 text-base"><span className="text-slate-300">Total</span><span className="font-display font-bold text-cyan-300">${price}{cycle === "annual" ? "/yr" : "/mo"}</span></div>
            </div>
            <p className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Zap className="h-3.5 w-3.5 text-cyan-400" /> Secure Stripe checkout. Test card 4242 4242 4242 4242. Tax calculated at checkout where applicable.</p>
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <button onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="wizard-back"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Back</button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} data-testid="wizard-next" className="inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#06080D] hover:bg-cyan-300">Continue <ArrowRight className="h-4 w-4" /></button>
          ) : (
            <button onClick={checkout} disabled={busy} data-testid="wizard-deploy" className="inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-6 py-2 text-sm font-semibold text-[#06080D] hover:bg-cyan-300 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Deploy Backup Server
            </button>
          )}
        </div>
      </GlassCard>
    </div>
  );
}
